/**
 * localStorage cache for sales-pack vault media. No TTL — invalidated only on
 * manual refresh or write-through after item edits / detach.
 */
import type { SalesMediaItem } from '../../../api/client';

// v2 invalidates old entries that may contain expired signed CDN URLs.
const VERSION = 'v2';
const SOFT_BYTE_LIMIT = 2 * 1024 * 1024; // ~2 MB per vault
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

export interface VaultCache {
  items: SalesMediaItem[];
  hasMore: boolean;
  nextOffset: number;
  cachedAt?: number;
}

function key(accountId: string, vaultId: string): string {
  return `sales_cache:${VERSION}:${accountId}:vault:${vaultId}`;
}

export function readVaultCache(
  accountId: string,
  vaultId: string
): VaultCache | null {
  try {
    const raw = localStorage.getItem(key(accountId, vaultId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VaultCache;
    if (!parsed || !Array.isArray(parsed.items)) return null;
    if (typeof parsed.cachedAt !== 'number' || Date.now() - parsed.cachedAt > MAX_AGE_MS) {
      clearVaultCache(accountId, vaultId);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeVaultCache(
  accountId: string,
  vaultId: string,
  cache: VaultCache
): void {
  try {
    const stamped: VaultCache = { ...cache, cachedAt: Date.now() };
    let payload = JSON.stringify(stamped);
    if (payload.length > SOFT_BYTE_LIMIT) {
      // Drop oldest items until we fit. Items are appended in fetch order, so
      // "oldest" = lowest offset = front of the array.
      const halved: VaultCache = {
        items: cache.items.slice(Math.floor(cache.items.length / 2)),
        hasMore: true,
        nextOffset: cache.nextOffset,
        cachedAt: stamped.cachedAt,
      };
      payload = JSON.stringify(halved);
    }
    localStorage.setItem(key(accountId, vaultId), payload);
  } catch {
    // QuotaExceeded — silently drop the cache for this vault.
    try {
      localStorage.removeItem(key(accountId, vaultId));
    } catch {
      // ignore
    }
  }
}

export function clearVaultCache(accountId: string, vaultId: string): void {
  try {
    localStorage.removeItem(key(accountId, vaultId));
  } catch {
    // ignore
  }
}

export function patchCachedItem(
  accountId: string,
  vaultId: string,
  contentId: string,
  patch: Partial<SalesMediaItem>
): void {
  const cache = readVaultCache(accountId, vaultId);
  if (!cache) return;
  let changed = false;
  const items = cache.items.map((it) => {
    if (it.id !== contentId) return it;
    changed = true;
    return { ...it, ...patch };
  });
  if (changed) {
    writeVaultCache(accountId, vaultId, { ...cache, items });
  }
}
